import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../context/AuthContext';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { Loader2, CheckCircle, ArrowLeft } from 'lucide-react';

const resetSchema = z.object({
  email: z.string().email('Voer een geldig e-mailadres in'),
});

type ResetFormData = z.infer<typeof resetSchema>;

export function PasswordResetForm() {
  const { resetPassword, error, clearError } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  });

  const onSubmit = async (data: ResetFormData) => {
    try {
      setIsSubmitting(true);
      clearError();
      await resetPassword(data.email);
      setResetSuccess(true);
    } catch (_err) {
      // Error is already set in context
    } finally {
      setIsSubmitting(false);
    }
  };

  if (resetSuccess) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            E-mail verzonden
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            We hebben een e-mail gestuurd met instructies om uw wachtwoord te resetten. 
            Controleer uw inbox en volg de instructies in de e-mail.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Als u geen e-mail heeft ontvangen, controleer dan uw spam folder.
          </p>
        </CardContent>
        <CardFooter>
          <Link to="/login" className="w-full">
            <Button className="w-full">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Terug naar inloggen
            </Button>
          </Link>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Wachtwoord resetten</CardTitle>
        <CardDescription>
          Voer uw e-mailadres in en we sturen u instructies om uw wachtwoord te resetten
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="email">E-mailadres</Label>
            <Input
              id="email"
              type="email"
              placeholder="naam@voorbeeld.nl"
              {...register('email')}
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSubmitting ? 'E-mail verzenden...' : 'Reset link versturen'}
          </Button>
          
          <Link 
            to="/login" 
            className="text-sm text-muted-foreground hover:text-primary flex items-center justify-center gap-1"
          >
            <ArrowLeft className="h-3 w-3" />
            Terug naar inloggen
          </Link>
        </CardFooter>
      </form>
    </Card>
  );
}